<template>
    <div class="confirm-bulk-send-modal">
        <div
            class="confirm-bulk-send-modal__content flex flex-col gap-10"
            :inert="isConfirmingWithoutEmails || null"
        >
            <ConfirmBulkSendModalSummary
                class="confirm-bulk-send-modal__summary"
                :invoices="invoices"
                :stagesMap="stagesMap"
            />

            <DataTable
                :value="invoices"
                responsiveLayout="scroll"
                scrollable
                scrollHeight="flex"
                class="invoices-table confirm-bulk-send-modal__table"
                stripedRows
            >
                <Column field="id" :sortable="false" style="min-width: 100px">
                    <template #header>
                        <div style="text-align: left; width: 100%">Invoice Number</div>
                    </template>
                    <template #body="{ data }">
                        <span class="invoice-id">{{ data.invoice_number }}</span>
                    </template>
                </Column>

                <Column field="type" :sortable="false" style="min-width: 150px">
                    <template #header>
                        <div style="text-align: center; width: 100%">Invoice Type</div>
                    </template>
                    <template #body="{ data }">
                        <div style="text-align: center">
                            <span class="invoice-type">{{ data.type }}</span>
                            <Tag
                                v-if="data.is_manual"
                                class="ml-2 manual-tag"
                                value="M"
                                severity="warning"
                            />
                        </div>
                    </template>
                </Column>

                <Column field="current_stage_name" :sortable="false" style="min-width: 100px">
                    <template #header>
                        <div style="text-align: center; width: 100%">Current Stage</div>
                    </template>
                    <template #body="{ data }">
                        <div style="text-align: center">
                            <Tag
                                :class="getStageTagClass(data.current_stage_name)"
                                :value="data.current_stage_name"
                            />
                        </div>
                    </template>
                </Column>

                <Column :sortable="false" style="width: 40px">
                    <template #body>
                        <div style="text-align: center">
                            <CoreIcon name="arrow-right" size="16" />
                        </div>
                    </template>
                </Column>

                <Column :sortable="false" style="min-width: 100px">
                    <template #header>
                        <div style="text-align: center; width: 100%">Next Stage</div>
                    </template>
                    <template #body="{ data }">
                        <div style="text-align: center">
                            <Tag
                                v-if="getNextStage(data.current_stage_name)"
                                :class="getStageTagClass(getNextStage(data.current_stage_name))"
                                :value="getNextStage(data.current_stage_name)"
                            />
                            <span v-else>-</span>
                        </div>
                    </template>
                </Column>

                <Column field="send_to_emails" :sortable="false" style="min-width: 200px">
                    <template #header>
                        <div style="text-align: left; width: 100%">{{ $t('§invoices.send_to') }}</div>
                    </template>
                    <template #body="{ data }">
                        <span v-if="data.send_to_emails" class="send-to-emails">
                            {{ data.send_to_emails }}
                        </span>
                        <CoreValueMissing v-else />
                    </template>
                </Column>

                <Column :sortable="false" style="min-width: 200px">
                    <template #header>
                        <div style="text-align: right; width: 100%">Action</div>
                    </template>
                    <template #body="{ data }">
                        <span class="action-text">{{ getActionText(data.current_stage_name) }}</span>
                    </template>
                </Column>
            </DataTable>

            <div class="confirm-bulk-send-modal__footer flex flex-col gap-10">
                <CoreCheckbox
                    v-model="doNotSendEmails"
                    name="doNotSendEmails"
                    label="§invoices.do_not_send_invoice_email"
                    :disabled="isConfirmingWithoutEmails"
                />

                <div class="flex justify-start gap-2">
                    <CoreButton
                        ref="sendButton"
                        :loading="invoicesStore.sendingMultiple"
                        :disabled="invoicesStore.sendingMultiple"
                        label="§ui_button.button_send_multiple_invoices"
                        @click="handleConfirm"
                        icon="pi-arrow-up-right"
                    />

                    <CoreButton
                        variant="tertiary"
                        :disabled="invoicesStore.sendingMultiple"
                        label="§ui_button.cancel"
                        @click="handleCancel"
                        icon="pi-times"
                    />
                </div>
            </div>
        </div>

        <Transition name="bulk-send-confirmation">
            <div v-if="isConfirmingWithoutEmails" class="confirm-bulk-send-modal__overlay">
                <div class="confirm-bulk-send-modal__confirmation">
                    <Message severity="warn" :closable="false">
                        {{ $t('§invoices.confirm_invoice_emails_will_not_be_sent') }}
                    </Message>

                    <div class="flex justify-start gap-2">
                        <CoreButton
                            :loading="invoicesStore.sendingMultiple"
                            :disabled="invoicesStore.sendingMultiple"
                            label="§ui_button.confirm"
                            @click="handleConfirmWithoutEmails"
                            icon="pi-check"
                        />

                        <CoreButton
                            ref="cancelConfirmationButton"
                            variant="tertiary"
                            :disabled="invoicesStore.sendingMultiple"
                            label="§ui_button.cancel"
                            @click="handleCancelConfirmation"
                            icon="pi-times"
                        />
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal.js'
import { useInvoicesStore } from '@/stores/invoices.js'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import CoreIcon from '@/components/core/CoreIcon.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import CoreValueMissing from '@/components/core/CoreValueMissing.vue'
import ConfirmBulkSendModalSummary from './ConfirmBulkSendModalSummary.vue'

export default {
    name: 'ConfirmBulkSendModal',

    components: {
        DataTable,
        Column,
        Tag,
        Message,
        CoreIcon,
        CoreButton,
        CoreCheckbox,
        CoreValueMissing,
        ConfirmBulkSendModalSummary,
    },

    data() {
        return {
            modalStore: useModalStore(),
            invoicesStore: useInvoicesStore(),
            invoices: [],
            isMonthlyUnsent: false,
            doNotSendEmails: false,
            isConfirmingWithoutEmails: false,

            stagesMap: {
                created: {
                    nextStage: 'dispatched',
                    action: '§invoices.send_invoice',
                },
                dispatched: {
                    nextStage: 'reminder',
                    action: '§invoices.payment_reminder_external',
                },
                reminder: {
                    nextStage: 'warning_1',
                    action: '§invoices.first_overdue_warning',
                },
                warning_1: {
                    nextStage: 'warning_2',
                    action: '§invoices.second_overdue_warning',
                },
                warning_2: {
                    nextStage: 'debt_collection',
                    action: '§invoices.send_to_debt_collection',
                },
            },
        }
    },

    watch: {
        isConfirmingWithoutEmails(isConfirming) {
            if (isConfirming) {
                window.addEventListener('keydown', this.handleConfirmationKeydown, true)
                this.$nextTick(() => this.$refs.cancelConfirmationButton?.$el?.focus())
                return
            }

            window.removeEventListener('keydown', this.handleConfirmationKeydown, true)
            this.$nextTick(() => this.$refs.sendButton?.$el?.focus())
        },
    },

    mounted() {
        this.invoices = this.modalStore.data.invoices || []
        this.isMonthlyUnsent = this.modalStore.data.isMonthlyUnsent || false
    },

    beforeUnmount() {
        window.removeEventListener('keydown', this.handleConfirmationKeydown, true)
    },

    methods: {
        getStageTagClass(stage) {
            const stageClasses = {
                created: 'stage-created',
                dispatched: 'stage-dispatched',
                reminder: 'stage-reminder',
                warning_1: 'stage-warning',
                warning_2: 'stage-warning',
                paid: 'stage-paid',
                debt_collection: 'stage-debt',
                canceled: 'stage-canceled',
            }

            return stageClasses[stage] || ''
        },

        getNextStage(currentStage) {
            return this.stagesMap[currentStage]?.nextStage || null
        },

        getActionText(currentStage) {
            return this.$t(this.stagesMap[currentStage]?.action) || '-'
        },

        handleCancel() {
            this.modalStore.setClose()
        },

        async handleConfirm() {
            if (this.doNotSendEmails) {
                this.isConfirmingWithoutEmails = true
                return
            }

            await this.sendInvoices()
        },

        async handleConfirmWithoutEmails(event) {
            if (event?.detail > 1) return

            await this.sendInvoices()
        },

        handleCancelConfirmation(event) {
            if (event?.detail > 1) return

            this.isConfirmingWithoutEmails = false
        },

        handleConfirmationKeydown(event) {
            if (event.key !== 'Escape') return

            event.stopPropagation()

            if (!this.invoicesStore.sendingMultiple) {
                this.isConfirmingWithoutEmails = false
            }
        },

        async sendInvoices() {
            const invoiceIDs = this.invoices.map((invoice) => invoice.id)

            await this.invoicesStore.sendMultipleInvoices({
                invoiceIDs,
                doNotSendEmails: this.doNotSendEmails,
            })

            this.modalStore.setClose()
        },
    },
}
</script>

<style scoped>
.confirm-bulk-send-modal {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    padding: 0;
}

.confirm-bulk-send-modal__content {
    flex: 1 1 auto;
    min-height: 0;
}

.confirm-bulk-send-modal__summary,
.confirm-bulk-send-modal__footer {
    flex-shrink: 0;
}

.confirm-bulk-send-modal__table {
    flex: 1 1 auto;
    min-height: 0;
}

.confirm-bulk-send-modal__overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2.4rem;
    border-radius: calc(var(--p-dialog-border-radius) - 1px);
    background: rgba(4, 15, 15, 0.3);
}

.confirm-bulk-send-modal__confirmation {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 100%;
    max-width: 52rem;
    max-height: 100%;
    overflow-y: auto;
    padding: 2.4rem;
    border-radius: var(--p-dialog-border-radius);
    background: var(--color-neutral-100);
    box-shadow: var(--p-dialog-shadow);
}

.bulk-send-confirmation-enter-active,
.bulk-send-confirmation-leave-active {
    transition: opacity 0.15s ease;
}

.bulk-send-confirmation-leave-active {
    pointer-events: none;
}

.bulk-send-confirmation-enter-from,
.bulk-send-confirmation-leave-to {
    opacity: 0;
}

.bulk-send-confirmation-enter-active .confirm-bulk-send-modal__confirmation,
.bulk-send-confirmation-leave-active .confirm-bulk-send-modal__confirmation {
    transition: transform 0.15s ease;
}

.bulk-send-confirmation-enter-from .confirm-bulk-send-modal__confirmation,
.bulk-send-confirmation-leave-to .confirm-bulk-send-modal__confirmation {
    transform: scale(0.96);
}

.send-to-emails {
    color: var(--text-dark);
    overflow-wrap: anywhere;
}

.modal-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.modal-title {
    font-size: 2rem;
    font-weight: 500;
    color: var(--text-dark);
    margin: 0;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-neutral-300);
}

.invoices-table {
    border-radius: 4px;
    overflow: hidden;
}

.invoice-id {
    color: var(--text-dark);
}

.invoice-type {
    font-size: 1.4rem;
    color: var(--text-dark);
}

.p-tag.p-component.manual-tag {
    font-size: 1.2rem;
    padding: 4px 6px;
    font-weight: 600;
    background-color: #fbc823;
}

.unpaid-amount {
    color: var(--text-dark);
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid var(--color-neutral-300);
}

.btn {
    padding: 10px 24px;
    border-radius: 6px;
    font-size: 1.4rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-secondary {
    background-color: var(--color-neutral-200);
    color: var(--text-dark);
}

.btn-secondary:hover {
    background-color: var(--color-neutral-300);
}

.btn-primary {
    background-color: var(--color-primary-600);
    color: white;
}

.btn-primary:hover {
    background-color: var(--color-primary-700);
}

/* PrimeVue Tag styling for stages */
:deep(.p-tag) {
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 1.3rem;
    font-weight: 400;
}

:deep(.stage-created) {
    background-color: var(--color-neutral-400);
    color: var(--text-dark);
}

:deep(.stage-dispatched) {
    background-color: var(--color-primary-500);
    color: white;
}

:deep(.stage-reminder) {
    background-color: var(--color-red-300);
    color: var(--text-dark);
}

:deep(.stage-warning) {
    background-color: var(--color-red-500);
    color: white;
}

:deep(.stage-paid) {
    background-color: var(--color-primary-800);
    color: white;
}

:deep(.stage-debt) {
    background-color: var(--color-secondary-900);
    color: white;
}

:deep(.stage-canceled) {
    background-color: var(--color-neutral-700);
    color: white;
}

/* DataTable row striping */
:deep(.p-datatable .p-datatable-tbody > tr:nth-child(odd)) {
    background-color: var(--color-neutral-100) !important;
}

:deep(.p-datatable .p-datatable-tbody > tr:nth-child(even)) {
    background-color: var(--color-neutral-400) !important;
}

/* Remove row borders */
:deep(.p-datatable .p-datatable-tbody > tr > td) {
    border: none;
}

/* Set all DataTable text to --text-dark */
:deep(.p-datatable) {
    color: var(--text-dark);
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
    color: var(--text-dark);
    font-weight: 500;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    color: var(--text-dark);
    font-weight: 300;
}

.manual-tag {
    font-size: 1.2rem;
    padding: 2px 6px;
    font-weight: 600;
}

/* Right align last column (Unpaid Amount) */
:deep(.p-datatable .p-datatable-thead > tr > th:last-child) {
    text-align: right !important;
}

:deep(.p-datatable .p-datatable-tbody > tr > td:last-child) {
    text-align: right !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .modal-title {
        font-size: 1.8rem;
    }

    .modal-actions {
        flex-direction: column;
    }

    .btn {
        width: 100%;
    }
}
</style>

<style>
.p-dialog:has(> .p-dialog-content > .confirm-bulk-send-modal) {
    position: relative;
}

.p-dialog-content:has(> .confirm-bulk-send-modal) {
    display: flex;
    flex-direction: column;
}
</style>
